import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, StopCircle } from 'lucide-react';
import { useAppStore, useIsSessionBusy, visibleError } from '@/stores/appStore';
import { useChatService } from '@/hooks/useChatService';
import { SessionTokenBadge } from './SessionTokenBadge';
import { ClusterModeSelector } from './ClusterModeSelector';
import { MentionSuggestions, mentionOptionId } from './MentionSuggestions';
import { useMentionSuggestions } from '@/hooks/useMentionSuggestions';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { MOD } from '@/utils/shortcuts';

export const MessageInput: React.FC = () => {
  const [input, setInput] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { currentSession } = useAppStore();
  // STORY-299: only the open chat's own turn blocks its input; a chat waiting
  // on an answer in another tab does not.
  const isBusy = useIsSessionBusy(currentSession?.id);
  // STORY-376: the not-saved banner belongs to its chat, not to the screen.
  const error = useAppStore(visibleError);
  const { sendMessage, stopGeneration } = useChatService();
  const { t, language } = useTranslation();
  // STORY-300: @cluster suggestions (the backend parses the mention).
  const mention = useMentionSuggestions({
    value: input, setValue: setInput, textareaRef, composing: isComposing,
  });

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 200); // Max height of 200px
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (input.trim() && !isBusy) {
      const message = input.trim();
      setInput('');
      await sendMessage(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // An open mention list owns ArrowUp/Down, Enter/Tab and Esc — Enter picks
    // a cluster there and sends only once the list is closed.
    if (mention.onKeyDown(e)) return;
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit(e);
    }
    // Command palette pass-through via global event
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      // Allow App to open palette
      // no-op here
    }
  };

  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = () => setIsComposing(false);

  const canSend = input.trim() && !isBusy;

  return (
    <div className="p-4 sticky bottom-0 z-10 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80 dark:supports-[backdrop-filter]:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700">
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      
      <ClusterModeSelector />
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-end space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                mention.syncCaret(e.target.selectionStart);
              }}
              onSelect={(e) => mention.syncCaret(e.currentTarget.selectionStart)}
              onBlur={() => mention.syncCaret(null)}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={mention.open}
              aria-controls={mention.open ? mention.listId : undefined}
              aria-activedescendant={
                mention.open ? mentionOptionId(mention.listId, mention.activeIndex) : undefined
              }
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              placeholder={language === 'ru' ? 'Введите сообщение... (Shift+Enter для новой строки)' : 'Type your message... (Shift+Enter for new line)'}
              className={clsx(
                'w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-3 pr-12 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors',
                'scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent'
              )}
              rows={1}
              style={{ minHeight: '48px' }}
              disabled={isBusy}
            />
            
            {mention.open && (
              <MentionSuggestions
                id={mention.listId}
                label={t.clusterMode?.mentionListLabel || 'Clusters to mention'}
                options={mention.options}
                activeIndex={mention.activeIndex}
                onSelect={mention.select}
                onHover={mention.setActiveIndex}
              />
            )}

            {/* Send button */}
            <div className="absolute right-2 bottom-2">
              {isBusy ? (
                <button
                  type="button"
                  onClick={stopGeneration}
                  className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                  title={t.chat.stop}
                >
                  <StopCircle className="h-5 w-5" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!canSend}
                  className={clsx(
                    'p-2 rounded-md transition-colors',
                    canSend
                      ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20'
                      : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  )}
                  title={language === 'ru' ? 'Отправить сообщение (Enter)' : 'Send message (Enter)'}
                >
                  <Send className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Loading indicator */}
        {isBusy && (
          <div className="flex items-center space-x-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t.chat.generating}</span>
          </div>
        )}
      </form>
      
      {/* Footer: Keyboard hints + Session token badge */}
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">Enter</kbd> {language === 'ru' ? 'отправить' : 'send'}
          <span className="mx-1.5 text-gray-300 dark:text-gray-600">|</span>
          <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">Shift+Enter</kbd> {language === 'ru' ? 'новая строка' : 'new line'}
          <span className="mx-1.5 text-gray-300 dark:text-gray-600">|</span>
          <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">{MOD}+K</kbd> {language === 'ru' ? 'команды' : 'commands'}
          <span className="mx-1.5 text-gray-300 dark:text-gray-600">|</span>
          <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">{MOD}+/</kbd> {language === 'ru' ? 'клавиши' : 'shortcuts'}
        </div>
        <SessionTokenBadge tokenUsage={currentSession?.tokenUsage} sessionId={currentSession?.id} />
      </div>
    </div>
  );
};